import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { DataTable } from "@/components/shared/DataTable";
import { StatusPill } from "@/components/shared/StatusPill";
import { EmptyState } from "@/components/shared/EmptyState";
import { FormModal } from "@/components/shared/FormModal";
import { useIndustries, useIndustryLabels } from "@/hooks/useIndustries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Building2, Plus, Search, SlidersHorizontal } from "lucide-react";
import { formatPhone } from "@/lib/phone";
import { toast } from "sonner";

const REL_OPTS = ["customer", "prospect", "vendor", "partner", "subcontractor", "supplier"].map((v) => ({ value: v, label: v }));

export default function Organizations() {
  const { has } = useAtlas();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [industryFilter, setIndustryFilter] = useState([]);
  const industries = useIndustries();
  const industryLabels = useIndustryLabels();
  const canManage = has("organizations.manage");

  const load = () => api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (v) => {
    await api.post("/organizations", { ...v, relationships: v.relationships || [] });
    toast.success("Organization created");
    load();
  };

  const toggleFilter = (id) => setIndustryFilter((f) => f.includes(id) ? f.filter((x) => x !== id) : [...f, id]);
  const filtered = orgs.filter((o) => o.name.toLowerCase().includes(q.toLowerCase()) && (industryFilter.length === 0 || industryFilter.includes(o.industry_id)));

  const columns = [
    { key: "name", header: "Organization", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
    { key: "industry", header: "Industry", render: (r) => industryLabels[r.industry_id] || <span className="text-slate-300">—</span> },
    { key: "relationships", header: "Relationships", render: (r) => (
        <div className="flex gap-1 flex-wrap">{(r.relationships || []).map((rel) => <StatusPill key={rel} value={rel} />)}{(!r.relationships || r.relationships.length === 0) && <span className="text-slate-300">—</span>}</div>
      ) },
    { key: "phone", header: "Phone", render: (r) => (r.phone && formatPhone(r.phone)) || <span className="text-slate-300">—</span> },
  ];

  return (
    <div>
      <PageHeader title="Organizations" subtitle="Shared Atlas Core records — reused across CRM, Inventory, Marketing & Tactics">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-8 w-48" data-testid="org-search" />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="org-industry-filter"><SlidersHorizontal className="w-4 h-4 mr-1" /> Industry{industryFilter.length > 0 && <span className="ml-1 text-xs bg-slate-900 text-white rounded-full px-1.5">{industryFilter.length}</span>}</Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 max-h-80 overflow-y-auto p-2">
            {industryFilter.length > 0 && <button onClick={() => setIndustryFilter([])} className="text-xs text-slate-500 hover:underline px-2 py-1" data-testid="org-industry-clear">Clear ({industryFilter.length})</button>}
            {industries.map((i) => (
              <label key={i.id} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-slate-50 cursor-pointer" data-testid={`org-industry-opt-${i.key}`}>
                <input type="checkbox" checked={industryFilter.includes(i.id)} onChange={() => toggleFilter(i.id)} />{i.label}
              </label>
            ))}
          </PopoverContent>
        </Popover>
        {canManage && <Button onClick={() => setOpen(true)} data-testid="org-create-button"><Plus className="w-4 h-4 mr-1" /> New Organization</Button>}
      </PageHeader>

      {orgs.length === 0 ? (
        <EmptyState icon={Building2} title="No organizations yet" description="Create your first organization. It will be a single shared record across every Atlas application." actionLabel={canManage ? "New Organization" : undefined} onAction={() => setOpen(true)} testid="org-empty" />
      ) : (
        <DataTable testid="crm-organizations-table" rowTestId="org-row" columns={columns} rows={filtered} onRowClick={(r) => navigate(`/crm/organizations/${r.id}`)} />
      )}

      <FormModal open={open} onOpenChange={setOpen} title="New Organization" testid="org-form-modal"
        onSubmit={create}
        fields={[
          { name: "name", label: "Name", required: true },
          { name: "industry_id", label: "Industry", type: "industry" },
          { name: "website", label: "Website" },
          { name: "phone", label: "Phone", type: "phone" },
          { name: "address", label: "Address" },
          { name: "relationships", label: "Business relationships", type: "multiselect", options: REL_OPTS },
        ]}
      />
    </div>
  );
}
